/**
 * The authenticated shell's guard.
 *
 * It decides during render and returns `<Navigate replace>` or `<Outlet />`. It does **not**
 * redirect from an effect: an effect runs after the commit, so the protected screen paints for
 * one frame before vanishing — a flash of someone else's sales list — and the navigation lands
 * twice in history.
 */

import { Navigate, Outlet, useLocation } from 'react-router';

import { selectStatus } from '@pos/shared';
import { logo } from '@pos/tokens';

import { useAppSelector } from '@/app/hooks';
import { PosShell } from '@/features/shell';
import { PosTour } from '@/features/tour';

export function ProtectedRoute() {
  const status = useAppSelector(selectStatus);
  const location = useLocation();

  if (status === 'restoring') return <BootScreen />;

  if (status === 'revoked') return <Navigate to="/device-revoked" replace />;

  if (status !== 'active') {
    // The attempted URL travels in navigation state, so signing in lands where the user was
    // going rather than dumping them on the sale screen every time.
    const attempted = `${location.pathname}${location.search}`;
    const target = status === 'locked' ? '/unlock' : '/sign-in';

    return <Navigate to={target} replace state={{ from: attempted }} />;
  }

  return (
    <PosShell>
      <Outlet />
      {/* Here rather than in the shell: only a signed-in person on a real page gets the tour. */}
      <PosTour />
    </PosShell>
  );
}

/**
 * Deliberately not a spinner. Boot is a session lookup that resolves in milliseconds; a spinner
 * would flash. This is the shell's own ground with the mark, so the first frame already looks
 * like the app.
 */
function BootScreen() {
  return (
    <div className="grid min-h-dvh place-items-center bg-surface-backdrop">
      {/* The product's mark, not a shop's: nobody is signed in yet, so there is no shop to have
          a look. Read from the token catalogue rather than written out here, per `brand.ts`. */}
      <img src={logo.mark} alt="" width={56} height={56} className="opacity-40" />
    </div>
  );
}
